import { useEffect, useMemo, useState } from "react"
import type { Dispatch, ReactNode } from "react"

import type { SegmentAnalysisAction, SegmentRunModalState } from "../hooks/use-segment-analysis"
import type {
  SegmentAnalysisDataSource,
  SegmentAnalysisRunActivity,
  SegmentAnalysisRunPayload,
} from "../types"
import type { ReasoningEffortOverride } from "@/stores/ai-explorer-store"

import { Check, Loader2, Play, X } from "@/components/icons"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { ReasoningModePill } from "@/features/chat/components/reasoning-mode-pill"
import { useAvailableGPTModels } from "@/hooks/use-config-query"

import { segmentAgentScript } from "../data/mock-segment-analysis-data"
import { safeHref } from "../utils/safe-href"

export type AgentRunConfirmOptions = {
  modelId?: string
  reasoningEffort?: string
}

type AgentRunModalProps = {
  currentRun: SegmentAnalysisRunPayload | null
  dataSource: SegmentAnalysisDataSource
  dispatch: Dispatch<SegmentAnalysisAction>
  runModal: SegmentRunModalState
  onConfirmRun: (options?: AgentRunConfirmOptions) => void
}

function getActivityName(id: string): string {
  switch (id) {
    case "queued":
      return "Queued"
    case "processing":
      return "Research pipeline"
    case "source-discovery":
      return "Source discovery"
    case "synthesis":
      return "Synthesis"
    default:
      return "Run activity"
  }
}

function getActivityTone(status: string): string {
  if (status === "failed") return "bg-destructive/15 text-destructive"
  if (status === "completed") return "bg-success/15 text-success"
  return "bg-primary/10 text-primary"
}

function getActivityProgress(status: string, isRunning: boolean): number {
  if (status === "failed" || status === "completed") return 1
  if (isRunning) return 0.64
  return 0.18
}

function getProgressColor(status: string): string {
  if (status === "failed") return "bg-destructive"
  if (status === "completed") return "bg-success"
  return "bg-primary"
}

function getStatusText({
  currentRun,
  done,
  failed,
  isBackendRun,
  sourceCount,
}: {
  currentRun: SegmentAnalysisRunPayload | null
  done: boolean
  failed: boolean
  isBackendRun: boolean
  sourceCount: number
}): string {
  if (failed) return currentRun?.error ?? "Run failed."
  if (done) return `Report updated from ${sourceCount} source${sourceCount === 1 ? "" : "s"}.`
  if (!isBackendRun) return "Working..."
  if (currentRun?.status === "queued") return "Queued in the backend research pipeline..."
  return "Researching with configured backend sources..."
}

function getFooterTone(failed: boolean, done: boolean): string {
  if (failed) return "text-destructive"
  if (done) return "text-success"
  return "text-muted-foreground"
}

export function AgentRunModal({
  currentRun,
  dataSource,
  dispatch,
  runModal,
  onConfirmRun,
}: AgentRunModalProps) {
  const isBackendRun = dataSource === "backend" && currentRun !== null
  const agents = useMemo(
    () => (runModal.mode === "partial" ? segmentAgentScript.slice(1, 3) : segmentAgentScript),
    [runModal.mode]
  )
  const [completedCount, setCompletedCount] = useState(0)
  // Same catalog as Explorer, including synthetic "auto" (default selection).
  const { models: chatModels } = useAvailableGPTModels("segment-analysis")
  // Reuse Explorer's ReasoningModePill (current models + Legacy submenu + effort).
  const [modelOverride, setModelOverride] = useState<string | null>(null)
  const [effortOverride, setEffortOverride] = useState<ReasoningEffortOverride | null>(null)
  // Default is Auto — backend omits modelId and uses first enabled deployment.
  const selectedModel = useMemo(() => {
    if (chatModels.length === 0) return null
    const fallback = chatModels.find((model) => model.id === "auto") ?? chatModels[0]
    const id = modelOverride ?? fallback.id
    return chatModels.find((model) => model.id === id) ?? fallback
  }, [chatModels, modelOverride])
  const done = isBackendRun ? currentRun.status === "completed" : completedCount >= agents.length
  const failed = isBackendRun && currentRun.status === "failed"
  const working = isBackendRun
    ? currentRun.status === "queued" || currentRun.status === "processing"
    : !done

  useEffect(() => {
    if (!runModal.isOpen || runModal.started) return
    // Reset to Auto each time the confirmation dialog opens.
    setModelOverride(null)
    setEffortOverride(null)
  }, [runModal.isOpen, runModal.started])

  useEffect(() => {
    if (!runModal.isOpen || !runModal.started || isBackendRun) return

    setCompletedCount(0)
    const timers = agents.map((_, index) =>
      window.setTimeout(
        () => setCompletedCount((current) => Math.max(current, index + 1)),
        550 * (index + 1)
      )
    )

    return () => {
      timers.forEach((timer) => window.clearTimeout(timer))
    }
  }, [agents, isBackendRun, runModal.isOpen, runModal.started])

  if (!runModal.isOpen || !runModal.mode) return null

  const handleOpenChange = (open: boolean) => {
    if (!open) dispatch({ type: "closeRunModal" })
  }

  // Confirmation step — a research run is expensive and (on the backend) produces
  // pending changes for review, so it requires an explicit "Start" rather than
  // firing the moment the modal opens.
  if (!runModal.started) {
    const isFull = runModal.mode === "full"
    return (
      <Dialog open={runModal.isOpen} onOpenChange={handleOpenChange}>
        <DialogContent
          className="max-w-md gap-0 overflow-hidden p-0"
          data-sa-surface=""
          onInteractOutside={(event) => event.preventDefault()}
        >
          <DialogHeader className="p-5 pb-0 text-left">
            <DialogTitle>{isFull ? "Run full report?" : "Run partial update?"}</DialogTitle>
            <DialogDescription>
              {isFull
                ? "Agents research this segment across configured sources. The result is queued as proposed changes for review — nothing goes live until approved."
                : "Refreshes the customer + competitor sections. The result is queued as proposed changes for review — nothing goes live until approved."}
            </DialogDescription>
          </DialogHeader>
          {selectedModel ? (
            <div className="p-5">
              <div className="border-border bg-muted/40 flex items-center justify-between gap-3 rounded-md border px-3 py-2">
                <span className="text-muted-foreground text-xs font-medium">Model</span>
                <ReasoningModePill
                  availableModels={chatModels}
                  contentClassName="z-[100]"
                  effortOverride={effortOverride}
                  modelOverride={modelOverride}
                  selectedModel={selectedModel}
                  onEffortOverrideChange={setEffortOverride}
                  onModelOverrideChange={setModelOverride}
                />
              </div>
            </div>
          ) : null}
          <DialogFooter className="border-border flex-row justify-end gap-2 border-t p-5 sm:space-x-0">
            <Button variant="ghost" onClick={() => dispatch({ type: "closeRunModal" })}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (!selectedModel) {
                  onConfirmRun()
                  return
                }
                // Auto → omit modelId so the backend picks the first enabled chat model.
                const modelId = selectedModel.id === "auto" ? undefined : selectedModel.id
                const supported = selectedModel.supported_efforts ?? []
                const defaultEffort = selectedModel.default_effort
                const effort =
                  (effortOverride && supported.includes(effortOverride)
                    ? effortOverride
                    : undefined) ??
                  (defaultEffort && supported.includes(defaultEffort) ? defaultEffort : undefined)
                onConfirmRun({
                  ...(modelId ? { modelId } : {}),
                  ...(effort ? { reasoningEffort: effort } : {}),
                })
              }}
            >
              <Play className="size-4" />
              {isFull ? "Start full run" : "Start partial run"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )
  }

  const sourceCount = currentRun?.sourceCount ?? currentRun?.sources.length ?? 0
  const capturedSources = isBackendRun ? currentRun.sources : []
  // The backend streams a "processing" announcement at the start of a phase and
  // (now) a follow-up "completed" update for the same activity id once it
  // finishes — dedupe by id, keeping the latest status, so that update replaces
  // the row in place instead of appending a second one stuck showing progress.
  const dedupedActivities = new Map<string, SegmentAnalysisRunActivity>()
  currentRun?.activities.forEach((activity) => dedupedActivities.set(activity.id, activity))
  const backendActivities = Array.from(dedupedActivities.values()).map((activity, index) => ({
    icon: activity.status === "failed" ? "!" : index + 1,
    id: activity.id,
    message: activity.message,
    name: getActivityName(activity.id),
    status: activity.status,
  }))
  const visibleBackendActivities =
    backendActivities.length > 0
      ? backendActivities
      : [
          {
            icon: 1,
            id: "queued",
            message: "Waiting for backend run status...",
            name: "Queued",
            status: currentRun?.status ?? "queued",
          },
        ]
  const statusText = getStatusText({ currentRun, done, failed, isBackendRun, sourceCount })
  let headerIcon = (
    <span className="bg-primary/10 text-primary thinking-breathe flex size-9 items-center justify-center rounded-full">
      <Loader2 className="size-5 animate-spin" />
    </span>
  )
  if (done) {
    headerIcon = (
      <span className="check-animated bg-success/15 text-success flex size-9 items-center justify-center rounded-full">
        <Check className="size-5" />
      </span>
    )
  } else if (failed) {
    headerIcon = (
      <span className="bg-destructive/15 text-destructive flex size-9 items-center justify-center rounded-full">
        <X className="size-5" />
      </span>
    )
  }
  let footerAction = (
    <Button variant="ghost" onClick={() => dispatch({ type: "closeRunModal" })}>
      {isBackendRun ? "Hide" : "Cancel"}
    </Button>
  )
  if (done) {
    // Backend runs produce pending changes for review (§5.4); mock runs publish directly.
    footerAction = isBackendRun ? (
      <Button onClick={() => dispatch({ type: "closeRunModal" })}>Review proposed changes</Button>
    ) : (
      <Button onClick={() => dispatch({ type: "finishRun", mode: runModal.mode ?? "full" })}>
        View report
      </Button>
    )
  } else if (failed) {
    footerAction = <Button onClick={() => dispatch({ type: "closeRunModal" })}>Close</Button>
  }

  return (
    <Dialog open={runModal.isOpen} onOpenChange={handleOpenChange}>
      <DialogContent
        className="flex max-h-[calc(100vh-2rem)] max-w-xl flex-col gap-0 overflow-hidden p-0"
        data-sa-surface=""
        // Backdrop clicks must not silently dismiss a running/reviewable agent
        // run; Escape and the explicit close remain the exits.
        onInteractOutside={(event) => event.preventDefault()}
      >
        <DialogHeader className="border-border flex-row items-start justify-between gap-4 space-y-0 border-b p-5 pr-12 text-left">
          <div className="flex gap-3">
            {headerIcon}
            <div>
              <DialogTitle>
                {runModal.mode === "partial"
                  ? "Partial update - customer + competitor"
                  : "Running full segment report"}
              </DialogTitle>
              <DialogDescription>
                {isBackendRun
                  ? "Backend agents are using configured web search, trusted sources, and model synthesis."
                  : "Agents working in parallel using configured priority sources."}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>
        <DialogBody className="p-5">
          <div className="grid gap-3">
            {isBackendRun
              ? visibleBackendActivities.map((activity) => {
                  const isDone = activity.status === "completed"
                  const isFailed = activity.status === "failed"
                  const isRunning = !isDone && !isFailed && working
                  const statusClass = getActivityTone(activity.status)
                  const progressScale = getActivityProgress(activity.status, isRunning)
                  const progressColor = getProgressColor(activity.status)
                  let icon: ReactNode = activity.icon
                  if (isDone) icon = <Check className="size-4" />
                  if (isFailed) icon = <X className="size-4" />

                  return (
                    <article
                      key={activity.id}
                      className="border-border grid grid-cols-[2rem_1fr] gap-3 rounded-lg border p-3"
                    >
                      <div
                        className={`flex size-8 items-center justify-center rounded-full text-xs font-bold ${statusClass}`}
                      >
                        {icon}
                      </div>
                      <div>
                        <h3 className="text-foreground text-sm font-semibold">{activity.name}</h3>
                        <p className="text-muted-foreground mt-0.5 text-sm">{activity.message}</p>
                        <div className="bg-muted mt-2 h-1.5 overflow-hidden rounded-full">
                          <div
                            className={`segment-progress-bar h-full w-full rounded-full ${progressColor}`}
                            style={{ transform: `scaleX(${progressScale})` }}
                          />
                        </div>
                      </div>
                    </article>
                  )
                })
              : agents.map((agent, index) => {
                  const isDone = index < completedCount
                  const isRunning = index === completedCount && !done
                  let message = "Queued."
                  let statusClass = "bg-muted text-muted-foreground"
                  let progressScale = 0
                  let progressColor = "bg-primary"
                  let icon: ReactNode = agent.icon

                  if (isDone) {
                    message = "Done."
                    statusClass = "bg-success/15 text-success"
                    progressScale = 1
                    progressColor = "bg-success"
                    icon = <Check className="size-4" />
                  } else if (isRunning) {
                    message =
                      agent.messages[Math.min(agent.messages.length - 1, completedCount)] ??
                      "Working..."
                    statusClass = "bg-primary/10 text-primary"
                    progressScale = 0.58
                  }

                  return (
                    <article
                      key={agent.id}
                      className="border-border grid grid-cols-[2rem_1fr] gap-3 rounded-lg border p-3"
                    >
                      <div
                        className={`flex size-8 items-center justify-center rounded-full text-xs font-bold ${statusClass}`}
                      >
                        {icon}
                      </div>
                      <div>
                        <h3 className="text-foreground text-sm font-semibold">{agent.name}</h3>
                        <p className="text-muted-foreground mt-0.5 text-sm">{message}</p>
                        <div className="bg-muted mt-2 h-1.5 overflow-hidden rounded-full">
                          <div
                            className={`segment-progress-bar h-full w-full rounded-full ${progressColor}`}
                            style={{ transform: `scaleX(${progressScale})` }}
                          />
                        </div>
                      </div>
                    </article>
                  )
                })}
            {capturedSources.length > 0 ? (
              <div className="border-border bg-muted/35 rounded-lg border p-3">
                <h3 className="text-foreground text-sm font-semibold">Sources captured</h3>
                <div className="mt-2 grid gap-2">
                  {capturedSources.slice(0, 3).map((source) => (
                    <a
                      key={source.url}
                      className="text-primary hover:text-primary/80 truncate text-sm font-medium transition"
                      href={safeHref(source.url)}
                      rel="noreferrer"
                      target="_blank"
                    >
                      {source.title}
                    </a>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        </DialogBody>
        <DialogFooter className="border-border flex-row items-center justify-between border-t px-5 py-3 text-sm sm:justify-between sm:space-x-0">
          <span className={getFooterTone(failed, done)}>{statusText}</span>
          {footerAction}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
